import { Button, Radio } from "antd";
import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import dayjs from "dayjs";
import PropagateLoader from "react-spinners/PropagateLoader";

import {
  calculateBlackScholesValue,
  calculateRemainingGrantOptionValues,
  calculateRemainingGrantRSUValues,
  calculateRunTotals,
  calculateTrancheTableRSUValues,
  calculateTrancheTableValues,
  getUpdatedTermLengths,
} from "./_10b51utils";
import StrategyModelingGraph from "./StrategyModelingGraph";
import StrategyStatsTable from "./StrategyStatsTable";

import optionStrategies from "./json/optionStrategies.json";
import rsuStrategies from "./json/rsuStrategies.json";

export default function StrategyPlanning({
  companyInfo,
  generalInfoParams,
  calculatedPlanValues,
  setCalculatedPlanValues,
  optionsCalculatorValues,
  RSUCalculatorValues,
  allTrancheTableValues,
  taxTableParams,
  committedSimulationParams,
  dateFormattedPricePaths,
}) {
  const [optionStrategySelected, setOptionStrategySelected] =
    useState("exerciseAll");
  const [rsuStrategySelected, setRsuStrategySelected] =
    useState("sellAtVestingDate");

  const [loading, setLoading] = useState(false);
  const [startModeling, setStartModeling] = useState(false);
  const [committedModelingParams, setCommittedModelingParams] = useState({});
  const hasCalculated = useRef(false);

  const getFullScheduleDetails = (scheduleDetails, currentAwardValues) => {
    const mappedTrancheDetails = (trancheDetails, trancheKey) => {
      return trancheDetails.map((tranche) => {
        return {
          ...tranche,
          ...currentAwardValues.find(
            (award) => award.ID === trancheKey.replace("Grant ", "grant"),
          ),
        };
      });
    };

    return Object.fromEntries(
      Object.entries(scheduleDetails).map(([trancheKey, trancheDetails]) => {
        return [
          trancheKey,
          [...mappedTrancheDetails(trancheDetails, trancheKey)],
        ];
      }),
    );
  };

  const getPriceAtTranche = useCallback(
    (trancheDate, iteration) => {
      const path = dateFormattedPricePaths[iteration];

      const afterIndex = path.findIndex((point) =>
        dayjs(point.date).isAfter(trancheDate),
      );

      if (afterIndex === -1) {
        return path[path.length - 1].value;
      } else if (afterIndex === 0) {
        return path[0].value;
      } else {
        const before = path[afterIndex - 1];
        const after = path[afterIndex];

        const t =
          trancheDate.diff(dayjs(before.date)) /
          dayjs(after.date).diff(dayjs(before.date));

        return before.value + t * (after.value - before.value);
      }
    },
    [dateFormattedPricePaths],
  );

  const calculateOptionValues = useCallback(
    (
      trancheDetails,
      updatedTermLengths,
      priceAtTranche,
      frozenDividendYield,
    ) => {
      const newOptionAwardValues = {
        ...trancheDetails,
        ...updatedTermLengths,
      };

      const newCalculatedBlackScholesValues = calculateBlackScholesValue({
        ...generalInfoParams?.[0],
        ...trancheDetails,
        sharePrice: priceAtTranche,
        dividendYield: frozenDividendYield / 100,
      });

      const newBlackScholesValues = {
        optionValue: newCalculatedBlackScholesValues?.callPriceEstimate,
        nD1: newCalculatedBlackScholesValues?.nD1
          ? newCalculatedBlackScholesValues.nD1 * 100
          : "",
      };
      const remainingGrantValues = calculateRemainingGrantOptionValues(
        { ...newOptionAwardValues, ...newBlackScholesValues },
        trancheDetails,
        [
          {
            ...generalInfoParams[0],
            sharePrice: priceAtTranche,
            dividendYield: frozenDividendYield / 100,
          },
        ],
        taxTableParams,
      );

      const updatedOptionValues = {
        ...newOptionAwardValues,
        ...newBlackScholesValues,
        ...remainingGrantValues,
        priceAtTranche: priceAtTranche,
      };

      const calaculatedTrancheTableValues = calculateTrancheTableValues(
        {
          ...generalInfoParams?.[0],
          ...updatedOptionValues,
          ...trancheDetails,
        },
        taxTableParams,
      );
      const newTrancheDetails = {
        ...trancheDetails,
        ...calaculatedTrancheTableValues,
      };

      return newTrancheDetails;
    },
    [generalInfoParams, taxTableParams],
  );

  const calculateRSUvalues = useCallback(
    (
      trancheDetails,
      updatedTermLengths,
      priceAtTranche,
      frozenDividendYield,
    ) => {
      const newRSUawardValues = {
        ...trancheDetails,
        ...updatedTermLengths,
      };

      const remainingGrantValues = calculateRemainingGrantRSUValues(
        newRSUawardValues,
        {
          ...generalInfoParams[0],
          sharePrice: priceAtTranche,
          dividendYield: frozenDividendYield / 100,
        },
        trancheDetails,
        taxTableParams,
      );
      const updatedRSUvalues = {
        ...newRSUawardValues,
        ...remainingGrantValues,
        priceAtTranche: priceAtTranche,
      };

      const calaculatedTrancheTableValues = calculateTrancheTableRSUValues(
        {
          ...generalInfoParams?.[0],
          ...updatedRSUvalues,
          ...trancheDetails,
        },
        taxTableParams,
        generalInfoParams,
      );
      const newTrancheDetails = {
        ...trancheDetails,
        ...calaculatedTrancheTableValues,
      };

      return newTrancheDetails;
    },
    [generalInfoParams, taxTableParams],
  );

  const calculatePlanValueOverTime = useCallback(
    (params) => {
      if (!params) return;

      const allIterationResults = [];

      for (
        let iteration = 0;
        iteration < (committedSimulationParams?.ITERATIONS || 100);
        iteration++
      ) {
        const revisedSchedulesWithValuations = {};
        // 0. Combine Option parameters with each of the tranche dates for that option return
        for (const [scheduleType, scheduleDetails] of Object.entries(
          allTrancheTableValues,
        )) {
          if (!["Options Schedule", "RSU Schedule"].includes(scheduleType))
            continue;

          const currentAwardValues =
            scheduleType === "Options Schedule"
              ? optionsCalculatorValues
              : RSUCalculatorValues;

          const fullScheduleDetails = getFullScheduleDetails(
            scheduleDetails,
            currentAwardValues,
          );

          if (!fullScheduleDetails || typeof fullScheduleDetails !== "object")
            return;
          // 1. Loop through all the tranche grants / schedules after tying them together above
          const revisedAwardSchedules = {};
          for (const [awardKey, fullTrancheDetails] of Object.entries(
            fullScheduleDetails,
          )) {
            // 2. Loop through the tranche dates
            const recalculatedTrancheDetails = [];
            let unvestedOptions = 0;
            for (const trancheDetails of fullTrancheDetails) {
              const trancheDate = dayjs(trancheDetails.trancheDate);
              // 3a. Determine price at date or linear interpolation for price at exact date
              const priceAtTranche = getPriceAtTranche(trancheDate, iteration);
              console.log(trancheDate, priceAtTranche);

              // 3b. FREEZE Dividend yield to the fixed number that it currently set
              const frozenDividendYield =
                generalInfoParams?.[0]?.dividendYield || 0;

              // 4. Run Option / RSU calculation to determine per-unit value at that price
              const updatedTermLengths = getUpdatedTermLengths({
                ...generalInfoParams?.[0],
                ...trancheDetails,
              });

              if (scheduleType === "Options Schedule") {
                trancheDetails["initialUnits"] += unvestedOptions;
                const updatedOptionValues = calculateOptionValues(
                  trancheDetails,
                  updatedTermLengths,
                  priceAtTranche,
                  frozenDividendYield,
                );

                //TODO: Turn this into a function based on strategy selected and return the updated object/unvested option amount if so
                if (
                  optionStrategySelected === "exerciseAll" &&
                  !priceAtTranche > trancheDetails?.strikePrice
                ) {
                  unvestedOptions += updatedOptionValues["initialUnits"];
                  updatedOptionValues["remainingFullValueAfterTax"] = 0;
                }

                // console.log(trancheDate.format("M/D/YY"), updatedOptionValues);
                // 5. Update tranche value extracted based on date, current valuation, and number of units available.
                recalculatedTrancheDetails.push(updatedOptionValues);
              }
              if (scheduleType === "RSU Schedule") {
                const updatedRSUvalues = calculateRSUvalues(
                  trancheDetails,
                  updatedTermLengths,
                  priceAtTranche,
                  frozenDividendYield,
                );

                recalculatedTrancheDetails.push(updatedRSUvalues);
              }
            }
            // 6. Now store this as a result - the date & the value recieved if the condition for selling is met
            revisedAwardSchedules[`${awardKey}`] = recalculatedTrancheDetails;
          }
          revisedSchedulesWithValuations[`${scheduleType}`] =
            revisedAwardSchedules;
        }
        allIterationResults.push(revisedSchedulesWithValuations);
      }
      return allIterationResults;
    },
    [
      generalInfoParams,
      optionsCalculatorValues,
      RSUCalculatorValues,
      allTrancheTableValues,
      committedSimulationParams,
      optionStrategySelected,
      getPriceAtTranche,
      calculateOptionValues,
      calculateRSUvalues,
    ],
  );

  // Start the modeling calculation/ loading states
  useEffect(() => {
    if (companyInfo?.Ticker && startModeling && !hasCalculated.current) {
      hasCalculated.current = true;

      const ticker = companyInfo.Ticker;
      const sharePrice = generalInfoParams?.[0]?.sharePrice || 0;

      const params = {
        ticker,
        generalInfoParams,
        sharePrice,
        optionStrategySelected,
        rsuStrategySelected,
      };

      setCommittedModelingParams(params);
      const result = calculatePlanValueOverTime(params);
      setCalculatedPlanValues(result);
      setLoading(false);
      setStartModeling(false);
    }
  }, [
    companyInfo,
    startModeling,
    loading,
    generalInfoParams,
    optionStrategySelected,
    rsuStrategySelected,
    setCalculatedPlanValues,
    calculatePlanValueOverTime,
  ]);

  const handleStartModeling = () => {
    hasCalculated.current = false;
    setCalculatedPlanValues();
    setStartModeling(true);
    setLoading(true);
  };

  useEffect(() => {
    if (!dateFormattedPricePaths) {
      setCalculatedPlanValues();
    }
  });

  // Calculate totals based on results for Options and RSUs
  const runTotals = useMemo(() => {
    return calculateRunTotals(calculatedPlanValues);
  }, [calculatedPlanValues]);

  return (
    <>
      {!dateFormattedPricePaths || !dateFormattedPricePaths?.length ? (
        <div className="col-lg-12" style={{ padding: 0 }} />
      ) : (
        <div className="col-lg-12" style={{ padding: 10 }}>
          <div style={{ padding: 10 }}>
            <h1 style={{ textAlign: "center" }}>Strategies</h1>
            <h3>Option Strategy:</h3>
            <Radio.Group
              onChange={(e) => setOptionStrategySelected(e.target.value)}
              value={optionStrategySelected}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                marginLeft: 15,
              }}
              options={optionStrategies.map((item) => ({
                label: item.disabled
                  ? `${item.label} (coming soon)`
                  : item.label,
                value: item.value,
                disabled: item.disabled,
              }))}
            />
          </div>
          <div style={{ padding: 10 }}>
            <h3>RSU Strategy:</h3>
            <Radio.Group
              onChange={(e) => setRsuStrategySelected(e.target.value)}
              value={rsuStrategySelected}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                marginLeft: 15,
              }}
              options={rsuStrategies.map((item) => ({
                label: item.disabled
                  ? `${item.label} (coming soon)`
                  : item.label,
                value: item.value,
                disabled: item.disabled,
              }))}
            />
          </div>
          <div style={{ margin: 20 }} />

          <div style={{ display: "flex", justifyContent: "center" }}>
            <Button onClick={() => handleStartModeling()} disabled={loading}>
              Start Modeling of Strategy Selected
            </Button>
          </div>
          {loading ? (
            <center
              style={{
                minHeight: "75px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PropagateLoader
                color={"var(--loader)"}
                loading={loading}
                size={17.5}
              />
            </center>
          ) : runTotals ? (
            <>
              <div style={{ marginTop: 15 }}>
                <h2>Distribution of Results</h2>
                <StrategyModelingGraph runs={runTotals} />
              </div>
              <div style={{ marginTop: 15 }}>
                <h2>Strategy Results Stats</h2>
                <StrategyStatsTable runs={runTotals} />
              </div>
            </>
          ) : (
            <></>
          )}
        </div>
      )}
    </>
  );
}
